import React, { useMemo } from "react";
import { Col, Row } from "reactstrap";
import { Component as Form } from "@repoflow/form-schema";
import { useSectionContext } from "@repoflow/components/section";
import ContentTabs from "@repoflow/components/content-tabs";
import { Form as TypesForm } from "@repoflow/components/types-form";
import { ValuesPane, typesHelp } from "components/types-help";
import PagePublicSchema from "@common/schema/page/public";
import TypesSchema from "@common/schema/types";
import { types as paramTypes } from "@common/schema/types/params/utils";
import { useWrite } from "forms/write";
import * as widgets from "widgets/register";

import { useEditorPage } from "../context";
import { localeOptions } from "../locales";

// The page itself, as opposed to what it is made of — and, under it, what this
// page changes about the document its site composes it into.
//
// Module scope, not a literal in JSX: a new `config` identity rebuilds the
// widget tree beneath the form's SchemaProvider and detaches the control being
// typed into (P139). That applies to all three of the lists below — and to the
// one list here that is not a constant, which is why `pageFields` is a
// function the screen memoises rather than a literal it rebuilds.
//
// `slug` and `body` are both gone from this form and from the row behind it.
// `slug` was descriptive in the way `route` was before anything served a page,
// nothing ever resolved a page by it, and gen-2's own Page tab did not carry it
// — two fields that both read as "the name in the URL" is a question the screen
// could not answer. `body` was not in gen-2's row at all, and nothing in
// common/render ever read it: a page is composed out of its ELEMENTS, so the
// wide box under the route invited an author to type a document into a field
// that could not reach the rendered page. See common/schema/page/fields.
//
// Exported so the suite beside this file drives the field list the screen
// renders rather than a copy of it, the same way CONFIG_FIELDS is.
export const pageFields = (localeOptionsFn) => ({
  "/Public/Page/Update": {
    fields: [
      "title",
      "route",
      // Beside `route` because it is the same decision: on this service a
      // page's language is part of the URL that names it.
      //
      // A PICKER over the site's declared codes when there are any, because
      // the codes are the only ones the service will accept — `locale` is
      // `$ref: /Site/Locale/Code` and `translate` refuses an undeclared one
      // outright — so a text box here is a field whose valid values are
      // written down on another screen. The options come from the site query
      // at the top of the editor and reach this through the editor page
      // context; ../locales is the option list and ../context says why it is
      // queried up there and not here (P139).
      //
      // And the plain pattern-validated string when the site declares none.
      // form-schema's `select` renders "No options" and NO control for an
      // empty list, which would leave a page whose locale predates the site's
      // declaring any with no way to read or clear it.
      localeOptionsFn
        ? { property: "locale", widget: "select", options: localeOptionsFn }
        : "locale",
      // `translationKey` IS NOT HERE, and there is no screen it is on.
      //
      // It is the field that says which pages are this one in another
      // language, and it is not a thing anybody types. `PageEntityMutations
      // .translate` mints it from the source's `pageId` when the source has
      // none, so what this box held for the life of the field was
      // `9606858d651b46c19947a7b255266205` — an id, in an editable control, on
      // the tab an author opens to change the page's title.
      //
      // A box like that is worse than a read-only one. It invites an edit, and
      // every edit of it is destructive in a way nothing on the screen
      // explains: clearing it drops the page out of its group, which stops the
      // rendered page emitting hreflang and splits it off the listing's row;
      // typing over it moves the page into a DIFFERENT group, silently, and
      // possibly one that already has a page in this language. Both are
      // reachable by an author who read the box as a name for something.
      //
      // So the value is now stated, never asked for: the page screen shows the
      // group as the other languages it holds (routes/.../pages/page/
      // translations.js), and the group itself is made by the Translate button.
      // Nothing writes this field but `translate`, which is what always did.
      //
      // What is deliberately gone with it is joining two independently written
      // pages into one group by hand — the one thing the box could do that
      // Translate cannot. It was never how anyone did it, and an id-shaped
      // control on every page's editor is a poor price for it; a picker that
      // named the OTHER PAGE would be the way back to it.
    ],
  },
});

// The page's half of the document shell. Same shape as the site's, which is the
// point: what a site declares here every page of it gets, and what a page
// declares here it gets instead — see `mergeConfig` in @common/render/page for
// which fields replace and which concatenate.
//
// Exported for the suite beside this file, which pins the two `widget:` names
// against the schema they are applied to. A copy of the list in the test would
// pin the copy.
export const CONFIG_TARGET = { $ref: "/PageConfig/Override" };

export const CONFIG_FIELDS = {
  "/PageConfig/Override": {
    // Ordered as the site's screen orders it — what identifies the document,
    // then what wraps its body, then what is injected around it — because the
    // two are the same fields and reading them in two different orders is how
    // an author stops trusting that they are.
    fields: [
      // Three labels differ from the schema's, and all three are the same
      // disambiguation the site's screen makes: the field beside them here is
      // the PAGE's, so the shell's has to say which document it means.
      // "Title" is the page's own, a few rows up.
      { property: "title", label: "Document title" },
      "description",
      "faviconUrl",
      "bodyClasses",
      "bodyAttributes",
      { property: "bodyTemplate", layout: "wide" },
      { property: "metadataList", layout: "wide" },
      // `widget: "list"` on both, for the reason the site's screen names: the
      // default for an array of string is `tags`, a chip per entry, which
      // cannot show a CDN path in full and splits one on a stray comma. The
      // name is on the screen and not on the schema field because a `widget:`
      // there is a demand on every screen that renders the type.
      { property: "includesStyle", layout: "wide", widget: "list" },
      { property: "includesScript", layout: "wide", widget: "list" },
      { property: "attributes", layout: "wide" },
      { property: "inlineHead", layout: "wide" },
      // "Site styles" / "Site scripts" in the schema, because a site is where
      // the field was first declared. On a page they are the page's.
      { property: "inlineStyle", layout: "wide", label: "Page styles" },
      { property: "inlineScript", layout: "wide", label: "Page scripts" },
    ],
  },
};

// Two forms mounted at once over one value object, which is what `useWrite` is
// for.
//
// A form-schema form seeds its internal state from `values` ONCE, at mount, and
// never re-seeds, so each of these holds a snapshot and every write it makes is
// a whole-object merge over that snapshot. Both write from an effect, so two
// can land in one React batch with no render between them and the second would
// win outright — taking the other's field with it, with nothing on screen to
// say so. See forms/write, and the site configuration screen, which is the same
// shape for the same reason.
//
// Neither form writes the other's half either: the page form's onChange
// destructures the three fields it owns and drops the stale `config` its
// snapshot is still holding, and `setConfig` writes only `config`.
export const Content = () => {
  const { schema, target, state } = useSectionContext();
  const { locales } = useEditorPage();
  const { values, setValues } = state;

  const write = useWrite(values, setValues);

  // Both memoised on `locales`, which is one memo above this on `data` — a new
  // identity for either rebuilds the widget tree beneath the form and detaches
  // the control being typed into (P139), and `title` and `route` are two of
  // the most typed-into controls in the app.
  const localeOptionsFn = useMemo(
    () => (locales.length > 0 ? localeOptions(locales) : null),
    [locales]
  );

  const fields = useMemo(() => pageFields(localeOptionsFn), [localeOptionsFn]);

  const config = values.config || {};
  const setConfig = (mod) => write({ config: mod });

  return (
    <>
      <Row>
        <Col>
          <Form
            schema={schema}
            target={target}
            widgets={widgets}
            values={values}
            // The three fields this form owns. `translationKey` is not among
            // them and is not dropped either: `values` still carries what the
            // query read, so the update sends the group unchanged.
            onChange={({ title, route, locale }) => write({ title, route, locale })}
            config={fields}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <h6 className="text-muted mt-4 mb-3">Document</h6>
          <ContentTabs
            tabs={[
              {
                label: "Configuration",
                content: (
                  <Form
                    schema={schema}
                    target={CONFIG_TARGET}
                    widgets={widgets}
                    values={config}
                    onChange={setConfig}
                    config={CONFIG_FIELDS}
                  />
                ),
              },
              {
                label: "Values",
                content: (
                  <ValuesPane
                    context="page"
                    types={config.types}
                    values={config.values}
                    widgets={widgets}
                    // The generated schema references /Image and friends by
                    // $ref, and a page's own types may too. Passing the page's
                    // public schema in means a param can use them without
                    // types-values knowing they exist.
                    utils={PagePublicSchema}
                    onChange={(mods) => setConfig({ ...config, values: mods })}
                  />
                ),
              },
              {
                label: "Types",
                content: (
                  <TypesForm
                    help={typesHelp("page")}
                    schema={TypesSchema}
                    paramTypes={paramTypes}
                    widgets={widgets}
                    values={{ types: config.types }}
                    onChange={({ types }) => setConfig({ ...config, types })}
                  />
                ),
              },
            ]}
          />
        </Col>
      </Row>
    </>
  );
};
